import { Fragment, useContext } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import { colors } from "@dotnet/telematics-design-system";

import CardItemValue from "components/TableDetails/components/CardItemValue";
import { TitleInfo } from "components/TableDetails/components/TitleInfo";
import { renderAvailableData } from "components/TableDetails/functions";
import { useSharedStyles } from "components/TableDetails/hooks";
import TableContext from "context/table/tableContext";
import { getEnvironment } from "shared";

import { CARD_ITEMS, CARDS } from "../../types";
import { isFtPlatform } from "../functions";
import { DetailsData } from "../types";

const ConfigurationDetailsAndSim = () => {
    const { gray200 } = colors;

    const { detailsData } = useContext(TableContext);

    const { t } = useTranslation();
    const { sharedStyles } = useSharedStyles();

    const { isProductionEnv } = getEnvironment();

    const {
        model_platform,
        one_wire_authorization_list,
        bluetooth_authorization_list,
        manual_geofence_list,
        current_configuration,
        gsm_number,
        iccid,
        imsi
    }: DetailsData = detailsData;

    const configurationDetailsList = [
        {
            title: CARD_ITEMS.MainConfiguration,
            value: current_configuration,
            isAvailable: true
        },
        {
            title: CARD_ITEMS.OneWireAuthorization,
            value: one_wire_authorization_list,
            isAvailable: true
        },
        {
            title: CARD_ITEMS.BluetoothAuthorization,
            value: bluetooth_authorization_list,
            isAvailable: true
        },
        {
            title: CARD_ITEMS.ManualGeofenceScenarios,
            value: manual_geofence_list,
            isAvailable: !isProductionEnv
        }
    ];

    const simList = [
        { title: CARD_ITEMS.GsmNumber, value: gsm_number, isAvailable: true },
        {
            title: CARD_ITEMS.Iccid,
            value: iccid,
            isAvailable: true
        },
        {
            title: CARD_ITEMS.Imsi,
            value: imsi,
            isAvailable: true
        }
    ];

    return (
        <div
            className="configuration-and-sim"
            css={css({
                fontSize: "14px",
                letterSpacing: "0.1px",
                padding: "0",

                "& > div": {
                    padding: "16px 24px",

                    ...sharedStyles
                }
            })}
        >
            {/* Configuration details - only for FT platform */}
            {isFtPlatform(model_platform) && (
                <div
                    css={css({
                        borderBottom: `1px solid ${gray200}`
                    })}
                >
                    <div>
                        {t(`Table##${CARDS.ConfigurationDetails}`)}

                        <TitleInfo
                            aria-label={t(
                                `Table##${CARDS.ConfigurationDetails}`
                            )}
                            cardName={CARDS.ConfigurationDetails}
                            list={configurationDetailsList}
                        />
                    </div>

                    <div>
                        {renderAvailableData(configurationDetailsList).map(
                            ({ title, value }) => {
                                return (
                                    <Fragment key={title}>
                                        <span>
                                            {t([
                                                `Table##${title}`,
                                                `General##${title}`
                                            ])}
                                            :
                                        </span>

                                        <CardItemValue value={value} />
                                    </Fragment>
                                );
                            }
                        )}
                    </div>
                </div>
            )}

            {/* SIM */}
            <div>
                <div>
                    {t(`Table##${CARDS.Sim}`)}

                    <TitleInfo
                        aria-label={t(`Table##${CARDS.Sim}`)}
                        cardName={CARDS.Sim}
                        list={simList}
                    />
                </div>

                <div>
                    {renderAvailableData(simList).map(({ title, value }) => {
                        return (
                            <Fragment key={title}>
                                <span>{t(`Table##${title}`)}:</span>
                                <CardItemValue value={value} />
                            </Fragment>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export { ConfigurationDetailsAndSim };
